<div class="title-bar title-bar--responsive">
  <div class="title-bar__heading">
    <h1>QUESTIONS</h1>
    <app-help screen="questions"></app-help>
  </div>
  <div class="title-bar__actions">
    @if (!editMode) {
      @if ((user | async)?.admin) {
        <button mat-stroked-button type="button" (click)="applyMinimumQuestions()">Apply minimum questions</button>
      }
      <button mat-flat-button color="primary" (click)="setMode('edit')" [disabled]="this.tabCode === 'ALL'">Edit Questions</button>
    }
    @if (editMode) {
      <button mat-flat-button color="accent" (click)="setMode('save')">Save Questions</button>
      <button mat-flat-button color="warn" (click)="setMode('cancel')">Cancel</button>
    }
    <app-aid-year [disabled]="editMode"></app-aid-year>
  </div>
</div>

<br />
@if (qs.draftQuestions | async | filter: filterFunction; as questions) {
  <mat-tab-group mat-stretch-tabs="false" (selectedTabChange)="onTabChanged($event)">
    @for (tab of tabs; track tab) {
      <mat-tab [label]="tab.label" [disabled]="tab.code === 'ALL' && editMode">
        @if (tab.code === 'SI' && questions.length === 0) {
          <div class="questions-empty">
            <mat-icon fontIcon="help_outline" aria-hidden="true"></mat-icon>
            <h2>No school specific questions</h2>
            <p>
              School specific questions collect extra information beyond the standard NPC questions.
              Add them only when you will use the answers in a population, package, table, or formula.
            </p>
            @if (!editMode) {
              <button mat-stroked-button color="primary" (click)="setMode('edit')">Edit Questions to add</button>
            }
            <p>
              If you need a question that is not on the optional list,
              <a href="https://form.collegeboard.org/f/npc-supplemental-question-application" target="_blank" rel="noopener" class="link">request a custom question</a>.
            </p>
          </div>
        } @else {
          @if (tab.code === 'SI') {
            <div class="warning-message">Only add questions that will be used in your populations, packages, tables, or formulas.</div>
          }
          <mat-card appearance="outlined">
            <mat-card-content>
              <div class="questions-table-wrapper">
                <table class="table table-hover">
                <thead>
                  <tr>
                    @if (editMode) {
                      <td style="width: 10rem; border: none"></td>
                    }
                    <th scope="col" aria-live="polite">
                      Question
                      @if (showHelp) {
                        <span class="sr-only">Column content has been updated with additional context</span>
                      }
                    </th>
                    <th scope="col" style="padding-left: 24px; width: 10rem">Format</th>
                    <th scope="col">
                      Custom Usage
                      <br />
                      <span class="legend-population">Population</span>
                      <span class="legend-table">Table</span>
                      <br />
                      <span class="legend-package">Package</span>
                      <span class="legend-formula">Formula</span>
                    </th>
                    @if (tab.code === 'ALL') {
                      <th scope="col" style="width: 10rem">Section</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (q of questions; track q) {
                    <tr (click)="clickedRow(q)">
                      @if (editMode) {
                        <td>
                          <button [disabled]="q.methodRequired && !(user | async)?.dev" mat-button (click)="removeQuestion(q)">
                            {{ q.methodRequired ? "required" : "remove" }}
                          </button>
                          @if (q.enhanceCalc) {
                            <span> <br /><small style="color: var(--admin-app-warn); margin-left: 1rem">refine calc</small> </span>
                          }
                        </td>
                      }
                      <th
                        scope="row"
                    [ngClass]="{
                      'triggered-question': q.triggerQuestionId !== undefined,
                      'deleted-question': q.dateDeleted,
                    }"
                        >
                        {{ q.dateDeleted ? "Deleted: " : "" }}{{ q.label }}
                        @if (editMode) {
                          <small><br />(ID: {{ q.id }})</small>
                        }
                        @if (showHelp) {
                          <div>
                            <span class="text-muted" style="font-size: 90%" [innerHTML]="q.help"></span>
                          </div>
                        }
                      </th>
                      <td>
                        @if (q.format !== 'Code') {
                          <div style="padding-left: 0.75rem">
                            {{ q.format === "Boolean" ? "Yes/No" : q.format }}
                          </div>
                        }
                        @if (q.format === 'Code') {
                          <div>
                            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                              <mat-label><span style="color: rgb(0, 0, 0)">Choices</span></mat-label>
                              <mat-select aria-readonly="true">
                                @for (choices of q.choices; track choices) {
                                  <mat-option>{{ choices.label }}</mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                          </div>
                        }
                      </td>
                      <td>
                        <app-usage entityType="Question" [entityId]="q.id"></app-usage>
                      </td>
                      @if (tab.code === 'ALL') {
                        <td>{{ q.section_id }}</td>
                      }
                    </tr>
                  }
                </tbody>
                </table>
              </div>
            </mat-card-content>
          </mat-card>
        }
        @if (editMode) {
          <div>
            @if (qs.unusedQuestions | async | filter: unusedFilterFunction; as unusedQuestions) {
              <div>
                @if (unusedQuestions.length !== 0) {
                  <div>
                    <mat-card appearance="outlined">
                      <mat-card-content>
                        <mat-card-title>
                          <h2>Optional Questions</h2>
                        </mat-card-title>
                        <mat-card-subtitle>
                          Select which questions to add and then save your selections.<br />
                          Only add questions to be used in packages, populations, tables, formulas or are designated as questions that will
                          <span style="color: var(--admin-app-warn)">refine your calc</span>.<br />
                          <br />If you require a question that is not on our list for packaging or population definition, you can <a href="https://form.collegeboard.org/f/npc-supplemental-question-application" target="_blank" class="link">request a custom question</a>.
                        </mat-card-subtitle>
                        <div class="questions-table-wrapper">
                          <table class="table table-hover">
                            <thead>
                              <tr>
                                <td style="width: 10rem; border: none"></td>
                                <th scope="col" aria-live="polite">
                                  Question
                                  @if (showHelp) {
                                    <span class="sr-only">Column content has been updated with additional context</span>
                                  }
                                </th>
                                <th scope="col" style="width: 10rem">Format</th>
                                @if (tab.code === 'ALL') {
                                  <th scope="col" style="width: 10rem">Section</th>
                                }
                              </tr>
                            </thead>
                            <tbody>
                              @for (q of unusedQuestions; track q) {
                                <tr>
                                  <td>
                                    <!-- TODO Allow for trigger questions to be added UNLESS trigger parent is in unused -->
                                    <button mat-button [disabled]="q.triggerQuestionId" (click)="addQuestion(q)">
                                      {{ q.triggerQuestionId ? "triggered" : "add" }}
                                    </button>
                                    @if (q.enhanceCalc) {
                                      <span> <br /><small style="color: var(--admin-app-warn); margin-left: 0.2rem">refine calc</small> </span>
                                    }
                                  </td>
                                  <th scope="row">
                                    {{ q.label }}
                                    @if (editMode) {
                                      <small><br />(ID: {{ q.id }})</small>
                                    }
                                    @if (showHelp) {
                                      <div>
                                        <span class="text-muted" style="font-size: 90%" [innerHTML]="q.help"></span>
                                      </div>
                                    }
                                  </th>
                                  <td>
                                    @if (q.format !== 'Code') {
                                      <div style="padding-left: 0.75rem">
                                        {{ q.format === "Boolean" ? "Yes/No" : q.format }}
                                      </div>
                                    }
                                    @if (q.format === 'Code') {
                                      <div>
                                        <mat-form-field appearance="outline" class="dropdown" subscriptSizing="dynamic">
                                          <mat-label><span style="color: rgb(0, 0, 0)">Choices</span></mat-label>
                                          <mat-select aria-readonly="true">
                                            @for (choices of q.choices; track choices) {
                                              <mat-option>{{ choices.label }}</mat-option>
                                            }
                                          </mat-select>
                                        </mat-form-field>
                                      </div>
                                    }
                                  </td>
                                  @if (tab.code === 'ALL') {
                                    <td>{{ q.section_id }}</td>
                                  }
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </div>
                }
              </div>
            }
          </div>
        }
      </mat-tab>
    }
  </mat-tab-group>
  @if (questions.length > 0 || editMode) {
    <span style="float: right">
      <mat-checkbox [(ngModel)]="showHelp">show help</mat-checkbox>
    </span>
  }
}
